import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import { Checkbox } from '../../../components/ui/Checkbox';

const OnboardingChecklist = ({ onTaskComplete }) => {
  const [tasks, setTasks] = useState([
    {
      id: 1,
      title: 'Complete your profile',
      description: 'Add your photo, bio, and contact information',
      xp: 50,
      completed: false,
      category: 'Getting Started'
    },
    {
      id: 2,
      title: 'Set up your workspace',
      description: 'Configure your development environment and tools',
      xp: 75,
      completed: false,
      category: 'Getting Started'
    },
    {
      id: 3,
      title: 'Meet your team',
      description: 'Schedule 1-on-1s with your direct team members',
      xp: 100,
      completed: false,
      category: 'Team Integration'
    },
    {
      id: 4,
      title: 'Review company values',
      description: 'Understand our mission, vision, and core principles',
      xp: 50,
      completed: false,
      category: 'Culture'
    },
    {
      id: 5,
      title: 'Complete security training',
      description: 'Learn about data protection and security protocols',
      xp: 100,
      completed: false,
      category: 'Compliance'
    },
    {
      id: 6,
      title: 'Submit your first code review',
      description: 'Get familiar with our code review process',
      xp: 150,
      completed: false,
      category: 'Technical'
    }
  ]);

  const [showAnimation, setShowAnimation] = useState(false);
  const [lastCompletedXP, setLastCompletedXP] = useState(0);

  const handleTaskToggle = (taskId) => {
    setTasks((prevTasks) =>
      prevTasks?.map((task) => {
        if (task?.id === taskId) {
          const newCompleted = !task?.completed;
          if (newCompleted) {
            setLastCompletedXP(task?.xp);
            setShowAnimation(true);
            setTimeout(() => setShowAnimation(false), 2000);
            if (onTaskComplete) {
              onTaskComplete(task?.xp);
            }
          }
          return { ...task, completed: newCompleted };
        }
        return task;
      })
    );
  };

  const completedCount = tasks?.filter((t) => t?.completed)?.length;
  const totalTasks = tasks?.length;
  const progressPercentage = (completedCount / totalTasks) * 100;

  const groupedTasks = tasks?.reduce((acc, task) => {
    if (!acc?.[task?.category]) {
      acc[task.category] = [];
    }
    acc?.[task?.category]?.push(task);
    return acc;
  }, {});

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Your Onboarding Journey</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Complete tasks to earn XP and level up
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-primary">{completedCount}/{totalTasks}</div>
          <div className="text-xs text-muted-foreground">Tasks completed</div>
        </div>
      </div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Overall Progress</span>
          <span className="text-sm text-muted-foreground">{Math.round(progressPercentage)}%</span>
        </div>
        <div className="relative h-3 bg-muted rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary to-warning rounded-full transition-all duration-500 ease-spring shadow-glow"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      {showAnimation && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 animate-scale-in">
          <div className="bg-success/20 text-success px-6 py-4 rounded-lg shadow-elevation-3 flex items-center gap-3 border-2 border-success/30">
            <Icon name="Star" size={32} color="var(--color-success)" strokeWidth={2.5} />
            <div>
              <div className="font-bold text-2xl">+{lastCompletedXP} XP</div>
              <div className="text-sm opacity-80">Great job!</div>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {Object.entries(groupedTasks)?.map(([category, categoryTasks]) => (
          <div key={category}>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              {category}
            </h3>
            <div className="space-y-3">
              {categoryTasks?.map((task) => (
                <div
                  key={task?.id}
                  className={`p-4 rounded-lg border transition-all duration-200 hover:shadow-elevation-1 ${
                    task?.completed
                      ? 'bg-success/5 border-success/20' :'bg-muted/20 border-border hover:border-primary/30'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={task?.completed}
                      onChange={() => handleTaskToggle(task?.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h4
                            className={`font-semibold text-base mb-1 ${
                              task?.completed ? 'text-muted-foreground line-through' : 'text-foreground'
                            }`}
                          >
                            {task?.title}
                          </h4>
                          <p className="text-sm text-muted-foreground">{task?.description}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-center gap-1 px-3 py-1 bg-primary/10 rounded-full">
                            <Icon name="Zap" size={14} color="var(--color-primary)" />
                            <span className="text-sm font-bold text-primary">{task?.xp} XP</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
        <div className="flex items-center gap-3">
          <Icon name="Trophy" size={24} color="var(--color-primary)" />
          <div>
            <p className="text-sm font-semibold text-foreground">Keep going!</p>
            <p className="text-xs text-muted-foreground">
              Complete all tasks to unlock the "Onboarding Champion" badge
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingChecklist;